import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for creating callback functions that have access to the component's latest state
 * and props. This is useful when the callback is created imperatively within an event handler
 * and needs to be called later.
 */
export const useAsyncCallback = <Args extends any[], Return>(
  callback: (...args: Args) => Return,
): ((...args: Args) => Return | void) => {
  const callbackRef = useRef(callback);
  const isMountedRef = useRef(false);
  // eslint-disable-next-line react-compiler/react-compiler
  callbackRef.current = callback;
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return useCallback(
    (...args: Args) => {
      if (isMountedRef.current) {
        return callbackRef.current(...args);
      }
    },
    [callbackRef],
  );
};
